import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Html5Qrcode } from 'html5-qrcode';
import { environment } from '../../../environments/environment';
import { AdminFeaturesService } from '../service/admin-features.service';

// QR ATTENDANCE (admin-verified). Admin opens a session, scans the student's QR with the
// camera, verifies the photo + details that come up, then marks attendance manually.
@Component({
  selector: 'app-attendance-admin',
  templateUrl: './attendance-admin.component.html',
  styleUrls: ['./attendance-admin.component.css'],
  providers: [MessageService],
})
export class AttendanceAdminComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  origin = environment.baseUrl.replace(/\/api\/?$/, '');   // for /upload photo paths

  events: any[] = [];
  sessions: any[] = [];
  newEventId: number | null = null;
  loading = false;

  /** Sessions are filtered by the event's own window, not by who left one open. */
  filter: 'live' | 'all' | 'ended' = 'live';

  // scan flow
  scanSession: any = null;      // session being scanned
  scanner: Html5Qrcode | null = null;
  scanning = false;
  camState: 'idle' | 'asking' | 'ready' | 'blocked' = 'idle';
  camHelp = '';
  looking = false;
  marking = false;
  card: any = null;             // lookup result (student verification card)
  scanError = '';
  /**
   * `students.id` per sport the admin has ticked to mark.
   *
   * Attendance is per game - the roster sheet has always had a Game selector -
   * so scanning a QR cannot mark "the student", only the sport(s) they are here
   * for. Everything not yet marked is pre-ticked, since the usual case is a
   * student turning up for all of them.
   */
  picked: number[] = [];

  // roll for the current session
  logs: any[] = [];

  /**
   * The just-marked student, held for a few seconds so the mark can be undone.
   *
   * The queue keeps moving, so a mis-scan has to be reversible on the spot -
   * hunting for it in the roll afterwards is not realistic at a check-in desk.
   */
  /** One scan can mark several sports, so undo reverses all of them together. */
  undo: { studentIds: number[]; name: string } | null = null;
  private undoTimer: any = null;

  constructor(private svc: AdminFeaturesService, private toast: MessageService, private zone: NgZone,
              private sanitizer: DomSanitizer) {}

  ngOnInit() {
    // Only this academic year's events - nobody starts an attendance session for
    // a meet that finished three years ago.
    this.svc.attendanceEvents().pipe(takeUntil(this.destroy$)).subscribe((e: any[]) => {
      this.events = (e || []).map(x => ({ id: x.id, name: x.name, startDate: x.startDate }));
    });
    this.load();
  }

  load() {
    this.loading = true;
    this.svc.listSessions().pipe(takeUntil(this.destroy$)).subscribe((r: any[]) => {
      this.sessions = r || []; this.loading = false;
    }, () => { this.loading = false; this.err('Failed to load sessions'); });
  }

  /** Live = today falls inside the event's own start/end window. */
  get visibleSessions(): any[] {
    if (this.filter === 'live')  return this.sessions.filter(s => s.isLive);
    if (this.filter === 'ended') return this.sessions.filter(s => !s.isLive);
    return this.sessions;
  }

  get liveCount(): number { return this.sessions.filter(s => s.isLive).length; }

  create() {
    if (!this.newEventId) { this.err('Pick an event'); return; }
    this.svc.createSession(this.newEventId).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.ok('Session started'); this.newEventId = null; this.load();
    }, () => this.err('Could not start session'));
  }

  toggle(s: any) {
    this.svc.setSessionActive(s.id, !s.isActive).pipe(takeUntil(this.destroy$)).subscribe(() => {
      s.isActive = !s.isActive;
      this.ok(s.isActive ? 'Session reopened' : 'Session ended');
      if (!s.isActive && this.scanSession && this.scanSession.id === s.id) this.closeScan();
    }, () => this.err('Update failed'));
  }

  /**
   * The fetched photo as a blob URL. Angular's sanitizer rewrites a raw blob:
   * URL in [src] to "unsafe:blob:", which renders nothing, so it is passed
   * through bypassSecurityTrustUrl. The raw string is kept alongside it purely
   * so the object URL can be revoked.
   */
  photoUrl: SafeUrl | null = null;
  private photoObjectUrl = '';

  photoSrc(url: string) { return url && url.indexOf('http') === 0 ? url : this.origin + url; }

  private loadPhoto(url: string | null) {
    this.releasePhoto();
    if (!url) return;
    this.svc.authedImage(this.photoSrc(url)).pipe(takeUntil(this.destroy$)).subscribe(
      (b: Blob) => this.zone.run(() => {
        this.photoObjectUrl = URL.createObjectURL(b);
        this.photoUrl = this.sanitizer.bypassSecurityTrustUrl(this.photoObjectUrl);
      }),
      () => { /* the empty-photo placeholder stands in */ },
    );
  }

  private releasePhoto() {
    if (this.photoObjectUrl) { URL.revokeObjectURL(this.photoObjectUrl); this.photoObjectUrl = ''; }
    this.photoUrl = null;
  }

  /**
   * An event running today that nobody has started a session for still shows in
   * the Live tab. Pressing Scan on it creates the session first, so the admin
   * never has to know the session exists.
   */
  startAndScan(row: any) {
    if (!row.pending) { this.openScan(row); return; }
    this.svc.createSession(row.eventId).pipe(takeUntil(this.destroy$)).subscribe((created: any) => {
      row.id = created && created.id; row.pending = false;
      this.ok('Session started');
      this.openScan(row);
      this.load();
    }, () => this.err('Could not start session'));
  }

  // ── Scan flow ──────────────────────────────────────────────────────────────
  openScan(session: any) {
    this.scanSession = session;
    this.card = null;
    this.scanError = '';
    this.camHelp = '';
    this.camState = 'idle';
    this.refreshLogs();
    // Wait for the modal's #qr-reader element to render, then ask for the camera.
    setTimeout(() => this.requestCamera(), 150);
  }

  /**
   * Ask the browser for the camera, then scan.
   *
   * getUserMedia is what raises the permission prompt, so it is called directly
   * rather than left to the QR library - a failure inside the library surfaces
   * as a generic error with no way to tell "denied" from "no camera" from
   * "page is not on https", which are three different fixes.
   */
  async requestCamera() {
    if (!this.scanSession) return;
    this.scanError = ''; this.camHelp = ''; this.camState = 'asking';

    const md = (navigator as any).mediaDevices;
    if (!md || !md.getUserMedia) {
      this.camState = 'blocked';
      this.scanError = 'This browser will not release the camera on an insecure page.';
      this.camHelp = location.protocol === 'https:' || location.hostname === 'localhost'
        ? 'Camera access is not available in this browser. Try Chrome or Safari.'
        : 'Open this page over https, or on the device itself as localhost. Browsers block the camera on plain http addresses like ' + location.host + '.';
      return;
    }

    try {
      // Held only long enough to raise the prompt; the QR library opens its own.
      const stream = await md.getUserMedia({ video: { facingMode: 'environment' } });
      stream.getTracks().forEach((t: any) => t.stop());
      this.camState = 'ready';
      this.startCamera();
    } catch (e) {
      this.zone.run(() => this.onCameraError(e));
    }
  }

  private onCameraError(e: any) {
    const name = (e && (e.name || e.constructor && e.constructor.name)) || '';
    this.scanning = false;
    this.camState = 'blocked';

    if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
      this.scanError = 'Camera permission was blocked for this site.';
      this.camHelp = 'Tap the padlock (or camera icon) in the address bar, set Camera to Allow, then press Try again. On iPhone: Settings > Safari > Camera > Allow.';
    } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
      this.scanError = 'No camera found on this device.';
      this.camHelp = 'Use a phone or a laptop with a camera. You can still mark attendance from Master > Attendance without scanning.';
    } else if (name === 'NotReadableError' || name === 'TrackStartError') {
      this.scanError = 'The camera is already in use by another app.';
      this.camHelp = 'Close Zoom, Meet, Photo Booth or any other tab using the camera, then press Try again.';
    } else {
      this.scanError = 'Could not start the camera.';
      this.camHelp = (e && e.message) ? String(e.message) : 'Press Try again.';
    }
  }

  private startCamera() {
    if (!this.scanSession) return;
    try {
      this.scanner = new Html5Qrcode('qr-reader');
      this.scanning = true;
      this.scanner.start(
        { facingMode: 'environment' },
        {
          fps: 12,
          /**
           * Scan region sized to the viewfinder, not a fixed 240px square.
           *
           * A constant box stayed small however large the video got, so on a
           * phone barely half the frame was being decoded and the desk had to
           * hold the code much closer than the camera actually needed. 82% of
           * the shorter side, with a floor so a small window still works.
           */
          qrbox: (w: number, h: number) => {
            const side = Math.max(160, Math.floor(Math.min(w, h) * 0.82));
            return { width: side, height: side };
          },
          aspectRatio: 1,
        },
        (decoded: string) => this.zone.run(() => this.onDecode(decoded)),
        () => { /* per-frame decode failures are normal; ignore */ },
      ).catch((e: any) => this.zone.run(() => this.onCameraError(e)));
    } catch (e) {
      this.onCameraError(e);
    }
  }

  private async stopCamera() {
    if (!this.scanner) return;
    try { if (this.scanning) await this.scanner.stop(); this.scanner.clear(); } catch { /* already stopped */ }
    this.scanner = null; this.scanning = false;
  }

  private onDecode(token: string) {
    if (this.looking || this.card) return;   // ignore repeat frames while resolving
    this.looking = true;
    this.stopCamera();
    this.svc.attendanceLookup(token, this.scanSession.id).pipe(takeUntil(this.destroy$)).subscribe(
      (r: any) => {
        this.looking = false; this.card = r;
        this.picked = ((r && r.registrations) || [])
          .filter((x: any) => !x.marked)
          .map((x: any) => x.studentId);
        this.loadPhoto(r && r.student ? r.student.photoUrl : null);
      },
      (e: any) => {
        this.looking = false;
        this.scanError = (e && e.error && e.error.error) ? e.error.error : 'Could not read this QR';
        // Deliberately NOT auto-rescanned. The screen used to reset itself after
        // 1.2s, which threw the message away before it could be read and looked
        // like the sheet had lost the scan on its own. Nothing leaves this state
        // now except the admin pressing Scan again or Close.
      },
    );
  }

  isPicked(r: any): boolean { return this.picked.indexOf(r.studentId) !== -1; }

  togglePick(r: any) {
    if (r.marked) { return; }
    const i = this.picked.indexOf(r.studentId);
    if (i === -1) { this.picked.push(r.studentId); } else { this.picked.splice(i, 1); }
  }

  /** Every sport at this event already through the desk - nothing left to do. */
  allMarked(): boolean {
    const regs = (this.card && this.card.registrations) || [];
    return regs.length > 0 && regs.every((r: any) => r.marked);
  }

  canMark(): boolean {
    if (!this.card) { return false; }
    const regs = (this.card.registrations || []);
    // With no per-sport rows (an unregistered scan) fall back to the single id,
    // so the screen still works rather than locking the button.
    if (!regs.length) { return !this.card.alreadyMarked; }
    return this.picked.length > 0;
  }

  mark() {
    if (!this.canMark() || this.marking) { return; }
    const ids = this.picked.length ? this.picked.slice() : [this.card.markStudentId];
    this.marking = true;
    this.svc.attendanceMark(this.scanSession.id, ids).pipe(takeUntil(this.destroy$)).subscribe(
      () => {
        this.marking = false;
        // Undo reverses the whole scan, so it carries every id that was marked.
        const marked = { studentIds: ids, name: this.card.student.name };
        const s = this.sessions.find(x => x.id === this.scanSession.id);
        if (s) s.scanCount = (s.scanCount || 0) + ids.length;

        // The camera comes straight back so the next student can step forward,
        // while the undo bar stays up for a few seconds over the top of it.
        this.showUndo(marked);
        this.rescan();
      },
      (e: any) => { this.marking = false; this.err((e && e.error && e.error.error) ? e.error.error : 'Mark failed'); },
    );
  }

  private showUndo(marked: { studentIds: number[]; name: string }) {
    this.undo = marked;
    if (this.undoTimer) { clearTimeout(this.undoTimer); }
    this.undoTimer = setTimeout(() => this.zone.run(() => { this.undo = null; }), 3000);
  }

  undoMark() {
    if (!this.undo || !this.scanSession) return;
    const target = this.undo;
    this.undo = null;
    if (this.undoTimer) { clearTimeout(this.undoTimer); this.undoTimer = null; }

    // One request per sport that was marked - undo mirrors the mark exactly.
    // The toast fires once the last one lands, not once per sport.
    let pending = target.studentIds.length;
    let failed = false;
    target.studentIds.forEach(id => {
      this.svc.attendanceUndo(this.scanSession.id, id).pipe(takeUntil(this.destroy$)).subscribe(
        () => {
          const s = this.sessions.find(x => x.id === this.scanSession.id);
          if (s && s.scanCount > 0) s.scanCount -= 1;
          if (--pending === 0) {
            if (!failed) { this.ok(`${target.name} - attendance undone`); }
            this.refreshLogs();
          }
        },
        (e: any) => {
          failed = true;
          this.err((e && e.error && e.error.error) ? e.error.error : 'Could not undo');
          if (--pending === 0) { this.refreshLogs(); }
        },
      );
    });
  }

  dismissUndo() {
    this.undo = null;
    if (this.undoTimer) { clearTimeout(this.undoTimer); this.undoTimer = null; }
  }

  rescan() {
    this.card = null;
    this.picked = [];
    this.releasePhoto();
    this.scanError = '';
    this.camHelp = '';
    this.refreshLogs();
    // Permission is already granted by this point, so go straight back to the
    // camera rather than raising a second prompt between students.
    setTimeout(() => (this.camState === 'ready' ? this.startCamera() : this.requestCamera()), 100);
  }

  private refreshLogs() {
    this.svc.sessionLogs(this.scanSession.id).pipe(takeUntil(this.destroy$)).subscribe((r: any[]) => this.logs = r || []);
  }

  closeScan() {
    this.stopCamera();
    this.releasePhoto();
    this.scanSession = null; this.card = null; this.scanError = ''; this.logs = [];
    this.load();
  }

  private ok(m: string)  { this.toast.add({ severity: 'success', summary: m }); }
  private err(m: string) { this.toast.add({ severity: 'error', summary: m }); }

  ngOnDestroy() {
    if (this.undoTimer) { clearTimeout(this.undoTimer); }
    this.stopCamera();
    this.releasePhoto();
    this.destroy$.next(); this.destroy$.complete();
  }
}
